import React, { FC, useCallback, useRef } from 'react';

type TDraggableHeaderCellProps =
  React.ThHTMLAttributes<HTMLTableHeaderCellElement> & {
    onColumnDragStart?: (index: number) => void;
    onColumnDragEnter?: (index: number) => void;
    onColumnDragEnd?: () => void;
  };

const DraggableHeaderCell: FC<TDraggableHeaderCellProps> = ({
  onColumnDragStart,
  onColumnDragEnter,
  onColumnDragEnd,
  children,
  className,
  style,
  ...restProps
}) => {
  const thRef = useRef<HTMLTableHeaderCellElement>(null);

  const getDraggableIndex = useCallback(() => {
    if (!thRef.current) return -1;
    const parent = thRef.current.parentElement;
    if (!parent) return -1;
    const draggable = Array.from(
      parent.querySelectorAll<HTMLElement>('th[draggable="true"]'),
    );
    return draggable.indexOf(thRef.current);
  }, []);

  const getDomIndex = useCallback(() => {
    if (!thRef.current) return -1;
    const parent = thRef.current.parentElement;
    if (!parent) return -1;
    return Array.from(parent.children).indexOf(thRef.current);
  }, []);

  const getTable = useCallback(() => {
    return thRef.current?.closest('table');
  }, []);

  const highlightColumn = useCallback(
    (colIndex: number, cls: string, add: boolean) => {
      const table = getTable();
      if (!table) return;
      table
        .querySelectorAll(`tr > *:nth-child(${colIndex + 1})`)
        .forEach(cell => {
          cell.classList.toggle(cls, add);
        });
    },
    [getTable],
  );

  const isNoDrag =
    className?.includes('no-drag') ||
    className?.includes('ant-table-selection-column') ||
    className?.includes('ant-table-row-expand-icon-cell');
  const isDraggable =
    !isNoDrag && onColumnDragStart && onColumnDragEnter && onColumnDragEnd;

  return (
    <th
      ref={thRef}
      className={className}
      {...restProps}
      draggable={!!isDraggable}
      onDragStart={e => {
        if (!isDraggable || !thRef.current) return;
        if (e.dataTransfer) {
          e.dataTransfer.effectAllowed = 'move';
        }
        const domIdx = getDomIndex();
        thRef.current.classList.add('column-dragging');
        highlightColumn(domIdx, 'column-dragging', true);
        onColumnDragStart(getDraggableIndex());
      }}
      onDragEnter={() => {
        if (!isDraggable || !thRef.current) return;
        const domIdx = getDomIndex();
        onColumnDragEnter(getDraggableIndex());
        highlightColumn(domIdx, 'column-drag-over', true);
      }}
      onDragLeave={() => {
        if (!thRef.current) return;
        const domIdx = getDomIndex();
        highlightColumn(domIdx, 'column-drag-over', false);
      }}
      onDragOver={e => {
        if (!isDraggable) return;
        e.preventDefault();
      }}
      onDragEnd={() => {
        if (!isDraggable || !thRef.current) return;
        const table = getTable();
        if (table) {
          table
            .querySelectorAll('.column-dragging, .column-drag-over')
            .forEach(cell => {
              cell.classList.remove('column-dragging', 'column-drag-over');
            });
        }
        onColumnDragEnd();
      }}
      style={{
        ...style,
        cursor: isDraggable ? 'grab' : undefined,
      }}
    >
      {children}
    </th>
  );
};

export default DraggableHeaderCell;
